/**
 * Auto-Extend Bidding Configuration Utility
 *
 * Centralized parsing and validation for auto-extend bidding parameters
 * from propertySpecialDetails. All fallback defaults match the current
 * hardcoded 15-minute behavior to prevent regressions.
 */

/**
 * Parses a string/boolean field to boolean.
 * Returns true for boolean true, or strings "true"/"y"/"yes"/"1" (case-insensitive).
 *
 * @param {*} value - The value to parse
 * @returns {boolean}
 */
export function parseBooleanField(value) {
  if (value === true) return true;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    return normalized === 'true' || normalized === 'y' || normalized === 'yes' || normalized === '1';
  }
  return false;
}

/**
 * Parses a string to a positive integer, returning defaultValue if invalid.
 * Returns defaultValue for null, undefined, empty string, non-numeric strings,
 * zero, or negative values.
 *
 * @param {*} value - The value to parse
 * @param {number} defaultValue - Fallback value when parsing fails
 * @returns {number}
 */
export function parsePositiveInt(value, defaultValue) {
  if (value === null || value === undefined || value === '') return defaultValue;
  const parsed = parseInt(value, 10);
  if (isNaN(parsed) || parsed <= 0) return defaultValue;
  return parsed;
}

/**
 * Builds tooltip text from autoExtendText or constructs a default.
 * Truncates to 500 characters.
 *
 * @param {*} autoExtendText - Custom tooltip text from API
 * @param {number} windowMinutes - Auto-extend window in minutes
 * @param {number} durationMinutes - Auto-extend duration in minutes
 * @returns {string}
 */
export function buildTooltipText(autoExtendText, windowMinutes, durationMinutes) {
  const MAX_LENGTH = 500;

  if (autoExtendText && typeof autoExtendText === 'string' && autoExtendText.trim().length > 0) {
    return autoExtendText.substring(0, MAX_LENGTH);
  }

  const defaultText = `If anyone bids in the last ${windowMinutes} minutes of the bidding period, the deadline will extend by ${durationMinutes} minutes.`;
  return defaultText.substring(0, MAX_LENGTH);
}

/**
 * Parses and validates auto-extend configuration from propertySpecialDetails.
 *
 * @param {Object} propertySpecialDetails - The propertySpecialDetails object from API
 * @returns {Object} Parsed auto-extend configuration
 */
export function parseAutoExtendConfig(propertySpecialDetails) {
  const config = propertySpecialDetails || {};

  const autoExtend = parseBooleanField(config.autoExtend);
  const sellerAutoExtendEnabled = parseBooleanField(config.sellerAutoExtendEnabled);
  const isAutoExtendActive = autoExtend && sellerAutoExtendEnabled;

  const autoExtendWindow = parsePositiveInt(config.autoExtendWindow, 15);
  const autoExtendDuration = parsePositiveInt(config.autoExtendDuration, 15);
  const maxExtensions = parsePositiveInt(config.maxExtensions, 0);

  const autoExtendText = buildTooltipText(
    config.autoExtendText,
    autoExtendWindow,
    autoExtendDuration
  );

  return {
    isAutoExtendActive,
    autoExtendWindow,
    autoExtendWindowMs: autoExtendWindow * 60 * 1000,
    autoExtendWindowSec: autoExtendWindow * 60,
    autoExtendDuration,
    autoExtendText,
    maxExtensions,
  };
}

/**
 * Returns the BID_AUTO_EXTD_MSG with dynamic duration.
 * Handles singular/plural "minute(s)".
 *
 * @param {number} duration - The auto-extend duration in minutes
 * @param {'success'|'error'} type - Message type
 * @returns {string} The formatted message
 */
export function getAutoExtendMessage(duration, type = 'error') {
  const validDuration = (Number.isInteger(duration) && duration > 0) ? duration : 15;
  const minuteWord = validDuration === 1 ? 'minute' : 'minutes';

  if (type === 'success') {
    return `Success! We have received your bid. The auction has been extended by ${validDuration} ${minuteWord} to allow for other bids and prevent sniping. In the meantime, you can view and manage your active bids.`;
  }

  return `The auction has been extended by ${validDuration} ${minuteWord} to allow for other bids and prevent sniping.`;
}
